import {
    PremiumFeatureTrial,
    getMainPremiumFeatureTrialRemainingTimes,
    getNodePremiumFeatureTrialRemainingTimes,
} from "@/services/api/premiumFeatureTrial";
import { observable, computed, makeObservable, actionBound } from "mobx";
import GlobalStore from "@/store";
import AppVersion from "@/enum/AppVersion";
import { groupBy } from "lodash";
import { axiosGroupByShopType } from "@/services/http/platformServer";

class PremiumFeatureTrialStore {
    constructor(public globalStore: typeof GlobalStore) {
        makeObservable(this, {
            premiumFeatureTrialMapping: observable,
            mainLoading: observable,
            nodeLoading: observable,
            loading: computed,
            getNodePremiumFeatureTrial: actionBound,
            getMainPremiumFeatureTrial: actionBound,
        });
    }

    premiumFeatureTrialMapping = new Map<number, PremiumFeatureTrial>();

    mainLoading = false;

    nodeLoading = false;

    get loading() {
        return this.mainLoading || this.nodeLoading;
    }

    async getNodePremiumFeatureTrial(shopIds: number[], cache = false) {
        const needLoadShopIds: number[] = [];

        for (const shopId of shopIds) {
            await this.globalStore.getShopAppVersion(shopId);

            const info = this.globalStore.shopAppVersionMapping.get(shopId);

            if (this.nodeLoading || info?.appVersion === AppVersion.PREMIUM || (cache && info)) continue;

            needLoadShopIds.push(shopId);
        }

        if (!needLoadShopIds.length) return;

        try {
            this.nodeLoading = true;

            const shopIdGroup = groupBy(needLoadShopIds, (shopId) => {
                const shopType = this.globalStore.shopMapping.get(shopId)?.type;

                return axiosGroupByShopType(shopType);
            });

            const groupShopIds = Object.values(shopIdGroup);

            for (const group of groupShopIds) {
                const results = await getNodePremiumFeatureTrialRemainingTimes(group);

                if (results.length) {
                    results.forEach((result) => {
                        const premiumFeatureTrial = this.premiumFeatureTrialMapping.get(result.shopId) || {};

                        this.premiumFeatureTrialMapping.set(result.shopId, {
                            ...premiumFeatureTrial,
                            ...result.premiumFeatureTrial,
                        });
                    });
                }
            }
        } finally {
            this.nodeLoading = false;
        }
    }

    async getMainPremiumFeatureTrial(shopIds: number[], cache = false) {
        const needLoadShopIds: number[] = [];

        for (const shopId of shopIds) {
            await this.globalStore.getShopAppVersion(shopId);

            const info = this.globalStore.shopAppVersionMapping.get(shopId);

            if (this.mainLoading || info?.appVersion === AppVersion.PREMIUM || (cache && info)) continue;

            needLoadShopIds.push(shopId);
        }

        if (!needLoadShopIds.length) return;

        try {
            this.mainLoading = true;

            const results = await getMainPremiumFeatureTrialRemainingTimes(needLoadShopIds);

            if (results.length) {
                results.forEach((result) => {
                    const premiumFeatureTrial = this.premiumFeatureTrialMapping.get(result.shopId) || {};

                    this.premiumFeatureTrialMapping.set(result.shopId, {
                        ...premiumFeatureTrial,
                        ...result.premiumFeatureTrial,
                    });
                });
            }
        } finally {
            this.mainLoading = false;
        }
    }
}

export default PremiumFeatureTrialStore;
